import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AUTH_URL } from '../utils/authApi';

// Check if WebAuthn is supported
function isWebAuthnSupported() {
  return window.PublicKeyCredential !== undefined;
}

// Check if platform authenticator is available (Face ID, Touch ID, Windows Hello)
async function isPlatformAuthenticatorAvailable() {
  if (!isWebAuthnSupported()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// Convert base64url to ArrayBuffer
function base64urlToBuffer(base64url) {
  const padding = '='.repeat((4 - base64url.length % 4) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const rawData = atob(base64);
  return Uint8Array.from(rawData, c => c.charCodeAt(0)).buffer;
}

// Convert ArrayBuffer to base64url
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (const byte of bytes) {
    str += String.fromCharCode(byte);
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export function BiometricSetup({ token, onClose }) {
  const [isSupported, setIsSupported] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deviceName, setDeviceName] = useState('');

  const checkSupport = async () => {
    const supported = await isPlatformAuthenticatorAvailable();
    setIsSupported(supported);
  };

  const loadCredentials = useCallback(async () => {
    try {
      const response = await axios.get(`${AUTH_URL}/api/auth/webauthn/credentials`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCredentials(response.data.credentials || []);
    } catch (err) {
      console.error('Failed to load credentials:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkSupport();
    loadCredentials();
  }, [loadCredentials]);

  const getDefaultDeviceName = () => {
    const ua = navigator.userAgent;
    if (ua.includes('iPhone')) return 'iPhone';
    if (ua.includes('iPad')) return 'iPad';
    if (ua.includes('Android')) return 'Android telefoon';
    if (ua.includes('Mac')) return 'Mac';
    if (ua.includes('Windows')) return 'Windows PC';
    return 'Mijn apparaat';
  };

  const handleRegister = async () => {
    setError('');
    setSuccess('');
    setIsRegistering(true);

    const name = deviceName || getDefaultDeviceName();

    try {
      // Get registration options
      const optionsResponse = await axios.get(
        `${AUTH_URL}/api/auth/webauthn/register-options?deviceName=${encodeURIComponent(name)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const options = optionsResponse.data;

      // Prepare WebAuthn options
      const publicKeyOptions = {
        challenge: base64urlToBuffer(options.challenge),
        rp: options.rp,
        user: {
          id: base64urlToBuffer(options.user.id),
          name: options.user.name,
          displayName: options.user.displayName,
        },
        pubKeyCredParams: options.pubKeyCredParams,
        timeout: options.timeout,
        attestation: options.attestation,
        authenticatorSelection: options.authenticatorSelection,
      };

      // Create credential (triggers biometric prompt)
      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions,
      });

      // Send credential to server
      const registerResponse = await axios.post(
        `${AUTH_URL}/api/auth/webauthn/register`,
        {
          credential: {
            id: credential.id,
            rawId: bufferToBase64url(credential.rawId),
            type: credential.type,
            response: {
              attestationObject: bufferToBase64url(credential.response.attestationObject),
              clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
              transports: credential.response.getTransports?.() || [],
            },
          },
          deviceName: name,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (registerResponse.data.success) {
        setSuccess(`${name} is ingesteld voor biometrische login!`);
        setDeviceName('');
        loadCredentials();
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Biometrische registratie geannuleerd');
      } else {
        setError(err.response?.data?.error || err.message || 'Registratie mislukt');
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleDelete = async (credentialId) => {
    if (!confirm('Weet je zeker dat je dit apparaat wilt verwijderen?')) return;

    try {
      await axios.delete(`${AUTH_URL}/api/auth/webauthn/credentials/${credentialId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadCredentials();
    } catch (err) {
      setError('Verwijderen mislukt');
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
          <p className="text-center">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Biometrische Login</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
            ×
          </button>
        </div>

        {!isSupported ? (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-lg">
            <p className="font-medium">Niet ondersteund</p>
            <p className="text-sm mt-1">
              Dit apparaat ondersteunt geen biometrische login (Face ID / vingerafdruk).
            </p>
          </div>
        ) : (
          <>
            {/* Registered devices */}
            {credentials.length > 0 && (
              <div className="mb-6">
                <h3 className="font-medium text-gray-700 mb-3">Geregistreerde apparaten</h3>
                <div className="space-y-2">
                  {credentials.map((cred) => (
                    <div
                      key={cred.id}
                      className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{cred.name}</p>
                        <p className="text-xs text-gray-500">
                          {cred.device_type} • {new Date(cred.created_at).toLocaleDateString('nl-NL')}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDelete(cred.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add new device */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-gray-700 mb-3">Apparaat toevoegen</h3>

              <input
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder={getDefaultDeviceName()}
                className="input-field mb-4"
              />

              <button
                onClick={handleRegister}
                disabled={isRegistering}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                {isRegistering ? (
                  'Bezig met instellen...'
                ) : (
                  <>
                    <span>🔐</span>
                    Face ID / Vingerafdruk instellen
                  </>
                )}
              </button>
            </div>

            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {success && (
              <div className="mt-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg">
                {success}
              </div>
            )}
          </>
        )}

        <button onClick={onClose} className="w-full btn-secondary mt-6">
          Sluiten
        </button>
      </div>
    </div>
  );
}
